import { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
// FP3-035F Issue 2 — Detect read-only shadow mode so admin can't
// mutate the schedule when viewing as a provider.
import { useReadOnlyAsProvider } from "@/components/layout/ReadOnlyProviderBanner";
import { CaretLeft, CaretRight, Eye, Wrench, Sparkle } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import OutsidePeriodCard from "@/components/schedule/OutsidePeriodCard";

import ScheduleView from "./schedule/ScheduleView";
import ScheduleBuilder from "./schedule/ScheduleBuilder";
import ScheduleMonthView from "./schedule/ScheduleMonthView";
import { PrintDisplayActions } from "@/components/print/PrintDisplayActions";
// Iter 32 — TeamSchedule import removed; Team tab no longer part of the
// provider's main schedule (school context now surfaces via the overlay
// toggle in Calendar/Builder).
import ConflictsPanel from "./schedule/ConflictsPanel";
import ScheduleWizard from "./schedule/ScheduleWizard";
// FP3-106C — Provider-owned one-click "Clear All" schedule reset.
import ClearAllScheduleModal from "@/components/schedule/ClearAllScheduleModal";
import ScheduleConstraints from "./schedule/ScheduleConstraints";
import ProviderConstraints from "./schedule/ProviderConstraints";
import HealthPanel from "./schedule/HealthPanel";
import SchoolYearTab from "./schedule/SchoolYearTab";
import { getWeekDates } from "./schedule/utils";

// Build the list of YYYY-MM-DD strings for every Mon-Fri in the month containing `d`.
function getMonthWeekdays(d) {
  const year = d.getFullYear();
  const month = d.getMonth();
  const last = new Date(year, month + 1, 0).getDate();
  const out = [];
  for (let day = 1; day <= last; day++) {
    const dt = new Date(year, month, day);
    const wd = dt.getDay(); // 0=Sun..6=Sat
    if (wd >= 1 && wd <= 5) {
      const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      out.push(iso);
    }
  }
  return out;
}

export default function Schedule() {
  const [searchParams, setSearchParams] = useSearchParams();
  const readOnly = useReadOnlyAsProvider();
  // FP3-075 — Simple Mode declutters the Builder header by hiding
  // the Wizard launch button. Users who want the Wizard can still
  // reach it by switching to Advanced Mode from /account/workspace,
  // or by navigating to /schedule?tab=builder directly. Existing
  // power users (preferences.ui_mode === "advanced", the default
  // for any pre-FP3-075 account) see no change.
  const { preferences } = useAuth();
  const isSimpleMode = preferences?.ui_mode === "simple";
  // Iter 28 — Health Panel deep-links arrive as ?tab=builder|constraints|health
  // and optional ?studentId=...&highlight=<block_id>. Initialize state from URL.
  const initialTab = ["view", "builder", "conflicts", "provider-rules", "school-rules", "school-year", "health"].includes(
    searchParams.get("tab")
  )
    ? searchParams.get("tab")
    : "view";
  const [activeTab, setActiveTab] = useState(initialTab);
  const highlightBlockId = searchParams.get("highlight") || null;
  const focusStudentId = searchParams.get("studentId") || null;
  const [provider, setProvider] = useState(null);
  const [allProviders, setAllProviders] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  // Iter 31 — Provider Calendar vs Shared School Calendar split.
  // Personal = my own self-overlaps; shared = my own-vs-another-provider
  // overlaps where my student or my room is involved. Tab badge uses the
  // personal count so the user sees their own actionable issues first.
  const [personalConflicts, setPersonalConflicts] = useState([]);
  const [sharedConflicts, setSharedConflicts] = useState([]);
  // Read-only school context overlay — other providers' sessions involving
  // my students. Toggleable in Builder/View.
  const [sharedSchoolSessions, setSharedSchoolSessions] = useState([]);
  const [showSchoolContext, setShowSchoolContext] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePeriod, setActivePeriod] = useState(null);

  // View tab state
  const [viewMode, setViewMode] = useState("week");
  const [viewDayIdx, setViewDayIdx] = useState(() => {
    const d = new Date().getDay();
    return d === 0 ? 4 : Math.min(d - 1, 4);
  });

  // Team tab state
  const [selectedCampus, setSelectedCampus] = useState("");
  const [teamDay, setTeamDay] = useState(0);

  // Week navigation
  const [baseDate, setBaseDate] = useState(new Date());
  const [builderResetSignal, setBuilderResetSignal] = useState(0);

  // Schedule Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardDraft, setWizardDraft] = useState(null); // {blocks: [...], appliedAt: number}
  // FP3-106C — One-click Clear All modal open state. Kept at the
  // page level so the reset callback can also wipe wizardDraft +
  // close the Wizard and bounce back to the Builder tab.
  const [clearAllOpen, setClearAllOpen] = useState(false);

  // Iter 28 — Health tab badge count (kept in sync by HealthPanel via callback)
  const [healthCount, setHealthCount] = useState(0);

  const weekDates = useMemo(() => getWeekDates(baseDate), [baseDate]);
  const ws = weekDates[0];
  const we = weekDates[4];

  // Iter 26 — Month view: fetch range expands to the whole month so the 4×5
  // grid pivot has every Mon-Fri available. baseDate's month drives this.
  const monthDates = useMemo(() => getMonthWeekdays(baseDate), [baseDate]);
  const ms = monthDates[0];
  const me = monthDates[monthDates.length - 1];

  // Effective fetch range — expands when month mode is selected.
  const fetchStart = viewMode === "month" ? ms : ws;
  const fetchEnd = viewMode === "month" ? me : we;

  // Conflict highlight set used to outline conflicting blocks in Builder/
  // View. Iter 31: only PERSONAL conflicts contribute to the red ring;
  // shared conflicts get an amber outline via sharedConflictSet so the
  // visual weight matches the new mental model.
  const conflictSet = useMemo(() => {
    const s = new Set();
    personalConflicts.forEach((c) => c.session_ids?.forEach((id) => s.add(id)));
    return s;
  }, [personalConflicts]);

  const sharedConflictSet = useMemo(() => {
    const s = new Set();
    sharedConflicts.forEach((c) => c.session_ids?.forEach((id) => s.add(id)));
    return s;
  }, [sharedConflicts]);

  // Is the viewed range within the active period? Uses the broader fetch
  // range so Month mode reflects month-overlap correctly.
  const isWithinActivePeriod = useMemo(() => {
    if (!activePeriod?.start_date || !activePeriod?.end_date) return false;
    return fetchEnd >= activePeriod.start_date && fetchStart <= activePeriod.end_date;
  }, [activePeriod, fetchStart, fetchEnd]);

  const loadData = useCallback(async () => {
    try {
      // Fetch current provider first so we can pass provider_id to the
      // conflicts endpoint and the new shared-school endpoint.
      const pRes = await api.getCurrentProvider();
      const myId = pRes.data?.id;

      const [apRes, sRes, tRes, cRes, perRes, ssRes] = await Promise.all([
        api.getProviders(),
        // Iter 45g — refuse to fall through to the unscoped students
        // endpoint. If we can't resolve the current provider we'd
        // rather show an empty list than silently leak the whole
        // school's roster into the Schedule builder.
        myId ? api.getStudents({ provider_id: myId }) : Promise.resolve({ data: [] }),
        api.getScheduleTeam({ start_date: fetchStart, end_date: fetchEnd }),
        api.getScheduleConflicts({
          start_date: fetchStart,
          end_date: fetchEnd,
          ...(myId ? { provider_id: myId } : {}),
        }),
        api.getActiveSchedulePeriod(),
        myId
          ? api.getSharedSchoolCalendar({
              start_date: fetchStart,
              end_date: fetchEnd,
              provider_id: myId,
            })
          : Promise.resolve({ data: { sessions: [] } }),
      ]);
      setProvider(pRes.data);
      setAllProviders(apRes.data);
      setAllStudents(sRes.data);
      setSessions(tRes.data?.sessions || []);
      // Backend returns the partitioned shape when provider_id is supplied.
      // Fall back to the flat list as personal-only when admin-mode is hit.
      const cBody = cRes.data || {};
      const personal = cBody.personal_conflicts ?? cBody.conflicts ?? [];
      const shared = cBody.shared_conflicts ?? [];
      setPersonalConflicts(personal);
      setSharedConflicts(shared);
      setConflicts([...personal, ...shared]);
      setActivePeriod(perRes.data && perRes.data.id ? perRes.data : null);
      setSharedSchoolSessions(ssRes.data?.sessions || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [fetchStart, fetchEnd]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Iter 28 — Sync activeTab with the ?tab= query param on every URL change
  // (otherwise navigating from one schedule subroute to another wouldn't
  // switch the tab — useState only seeds once on mount).
  useEffect(() => {
    const t = searchParams.get("tab");
    if (
      t &&
      ["view", "builder", "conflicts", "provider-rules", "school-rules", "school-year", "health"].includes(t) &&
      t !== activeTab
    ) {
      setActiveTab(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Iter 28 — Background fetch for the Health tab badge so the count is
  // visible without the user having to open the tab. Refreshes whenever the
  // provider or active period changes.
  useEffect(() => {
    if (!provider?.id) return;
    let cancelled = false;
    const params = { provider_id: provider.id };
    if (activePeriod?.id) params.schedule_period_id = activePeriod.id;
    api
      .getScheduleHealthCheck(params)
      .then((r) => {
        if (!cancelled) setHealthCount(r.data?.summary?.total_violations || 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [provider?.id, activePeriod?.id]);

  // Sessions for current provider (used by View tab)
  const mySessions = useMemo(() => sessions.filter((s) => s.provider_id === provider?.id), [sessions, provider]);

  const navWeek = (dir) => {
    const d = new Date(baseDate);
    if (viewMode === "month") {
      // Iter 26 — Month nav steps by a calendar month
      d.setMonth(d.getMonth() + dir, 1);
    } else {
      d.setDate(d.getDate() + dir * 7);
    }
    setBaseDate(d);
    setBuilderResetSignal((n) => n + 1);
  };

  const handleTabChange = (t) => {
    setActiveTab(t);
    if (t !== "builder") setBuilderResetSignal((n) => n + 1);
    // Iter 28 — keep ?tab= in sync; drop deep-link params on user navigation
    // so a stale highlight/studentId doesn't follow them after the click.
    const next = new URLSearchParams();
    next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  // FP3-112E — Schedule-section-specific bounded shell.
  //   • Canvas tabs (Calendar / Builder) cap at 1520px so the week
  //     grid stays proportional and doesn't stretch the whole page
  //     edge-to-edge; canvas still gets a comfortable ~1264px on
  //     1536 viewports and grows to 1520px on 1760+.
  //   • Form/report tabs (Provider Rules / School Rules / School
  //     Year / Health / Conflicts) cap at 1360px so form fields
  //     don't balloon.
  //   • Left-aligned (no `mx-auto`) — content hugs the sidebar;
  //     extra shell width becomes calm right-side margin.
  const isCanvasTab = activeTab === "view" || activeTab === "builder";
  const scheduleShellClass = isCanvasTab
    ? "px-6 py-6 max-w-[1520px] animate-fade-in"
    : "px-6 py-6 max-w-[1360px] animate-fade-in";

  if (loading) {
    return (
      <div className={`${scheduleShellClass} space-y-4`} data-testid="schedule-loading">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-8 w-full" />
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className={scheduleShellClass} data-testid="schedule-page">
      {/* FP3-112E — Header + Print/Display cluster grouped on the
          LEFT (no `justify-between`, no `ml-auto`). Controls appear
          directly under the Schedule title / period line so operators
          don't lose them on the far right edge of a 1520px shell. */}
      <div className="mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#1F2937] heading-font tracking-tight" data-testid="schedule-title">
            Schedule
          </h1>
          {activePeriod && (
            <p className="text-xs text-[#6B7280] mt-1" data-testid="period-header">
              <span className="font-medium text-[#4B5563]">{activePeriod.name}</span>
              <span className="mx-2 text-[#9B9890]">·</span>
              <span>
                {new Date(activePeriod.start_date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                {" – "}
                {new Date(activePeriod.end_date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </span>
            </p>
          )}
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="schedule-header-actions">
          {/* FP3-033 — Print / Display the current provider's schedule.
              Buttons only render once we know the provider id. */}
          {provider?.id && (
            <PrintDisplayActions
              printHref={`/print/provider/${provider.id}/schedule?view=week`}
              displayHref={`/display/provider/${provider.id}/schedule`}
              label="Provider schedule output"
              testIdPrefix="schedule"
            />
          )}
          {/* FP3-075D — The yellow "Week is outside active period"
              header chip is fully redundant with the new
              OutsidePeriodCard (which shows the same fact + setup
              CTAs in the canvas). Suppress it in BOTH modes to
              avoid duplicate / conflicting messaging. */}
          {false && !isWithinActivePeriod && activePeriod && (
            <Badge
              className="bg-[#FDF8F3] text-[#D9A05B] hover:bg-[#FDF8F3] text-[11px] px-2 py-1"
              data-testid="outside-period-badge"
            >
              Week is outside active period
            </Badge>
          )}
        </div>
      </div>
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        {/* FP3-112E — Section heading + week/nav/action controls
            now share a compact left-aligned row that wraps naturally
            at narrower widths. Dropped `justify-between` so Wizard /
            Prev / Today / Next / School Context / Clear All never
            get pushed to the far-right edge of the shell. */}
        <div className="flex items-center gap-x-3 gap-y-2 mb-5 flex-wrap">
          {/* Iter 32 — Schedule top bar simplified. Section navigation
              (Calendar / Builder / Provider Rules / School Rules / Health)
              moved into the left sidebar sub-nav. The top bar now only
              shows the active section name + week navigator + actions. */}
          <h2
            data-testid="schedule-section-heading"
            className="text-base font-semibold text-[#1F2937] heading-font"
          >
            {(() => {
              switch (activeTab) {
                case "builder": return "Schedule · Builder";
                case "provider-rules": return "Schedule · Provider Rules";
                case "school-rules": return "Schedule · School Rules";
                case "school-year": return "Schedule · School Year";
                case "health": return (
                  <span className="flex items-center gap-2">
                    Schedule · Health
                    {healthCount > 0 && (
                      <span
                        data-testid="health-section-count"
                        className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#D9A05B] text-white text-[10px] font-bold"
                      >
                        {healthCount}
                      </span>
                    )}
                  </span>
                );
                case "conflicts": return (
                  <span className="flex items-center gap-2">
                    Schedule · Conflicts
                    {personalConflicts.length > 0 && (
                      <span
                        data-testid="conflicts-section-personal-count"
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#B85C5C] text-white text-[10px] font-bold"
                      >
                        {personalConflicts.length}
                      </span>
                    )}
                    {sharedConflicts.length > 0 && (
                      <span
                        data-testid="conflicts-section-shared-count"
                        className="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-[#FDF8F3] text-[#D9A05B] text-[9px] font-bold border border-[#D9A05B]"
                      >
                        {sharedConflicts.length}
                      </span>
                    )}
                  </span>
                );
                default: return "Schedule · Calendar";
              }
            })()}
          </h2>
          {/* Hidden TabsList drives Radix state — visible navigation is
              now in the sidebar. Kept for state semantics only. */}
          <TabsList className="hidden">
            <TabsTrigger value="view" data-testid="tab-view" />
            <TabsTrigger value="builder" data-testid="tab-builder" />
            <TabsTrigger value="conflicts" data-testid="tab-conflicts" />
            <TabsTrigger value="provider-rules" data-testid="tab-provider-rules" />
            <TabsTrigger value="school-rules" data-testid="tab-school-rules" />
            <TabsTrigger value="school-year" data-testid="tab-school-year" />
            <TabsTrigger value="health" data-testid="tab-health" />
          </TabsList>
          <div className="flex items-center gap-2">
            {/* Iter 32 — School context overlay toggle. Visible on View/
                Builder where ghost overlay is rendered. Hidden on rules/
                health where it would do nothing. */}
            {(activeTab === "view" || activeTab === "builder") && (
              <button
                data-testid="school-context-toggle"
                onClick={() => setShowSchoolContext((v) => !v)}
                title={
                  showSchoolContext
                    ? "Hide other providers' sessions involving your students"
                    : "Show other providers' sessions involving your students (read-only)"
                }
                className={`flex items-center gap-1.5 px-2.5 h-7 rounded-lg border text-[11px] font-medium transition-colors mr-1 ${
                  showSchoolContext
                    ? "bg-[#FDF8F3] text-[#9C6B2E] border-[#D9A05B]"
                    : "bg-white text-[#6B7280] border-[#E5E3DB] hover:bg-[#F3F1EC]"
                }`}
              >
                <span
                  className={`inline-block w-2 h-2 rounded-full ${
                    showSchoolContext ? "bg-[#D9A05B]" : "bg-[#d4d2cc]"
                  }`}
                />
                School context{sharedSchoolSessions.length > 0 && ` (${sharedSchoolSessions.length})`}
              </button>
            )}
            {activeTab === "builder" && isWithinActivePeriod && !isSimpleMode && (
              <Button
                onClick={() => setWizardOpen(true)}
                size="sm"
                data-testid="open-wizard"
                className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg h-7 text-xs mr-1"
              >
                <Sparkle size={12} weight="duotone" className="mr-1" />
                Wizard
              </Button>
            )}
            {activeTab === "view" && (
              <div className="flex bg-[#F3F1EC] rounded-lg p-0.5 mr-2">
                <button
                  data-testid="view-mode-day"
                  onClick={() => setViewMode("day")}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                    viewMode === "day" ? "bg-white text-[#1E3F33] shadow-sm" : "text-[#6B7280]"
                  }`}
                >
                  Day
                </button>
                <button
                  data-testid="view-mode-week"
                  onClick={() => setViewMode("week")}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                    viewMode === "week" ? "bg-white text-[#1E3F33] shadow-sm" : "text-[#6B7280]"
                  }`}
                >
                  Week
                </button>
                <button
                  data-testid="view-mode-month"
                  onClick={() => setViewMode("month")}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                    viewMode === "month" ? "bg-white text-[#1E3F33] shadow-sm" : "text-[#6B7280]"
                  }`}
                >
                  Month
                </button>
              </div>
            )}
            <button
              data-testid="schedule-nav-prev"
              onClick={() => navWeek(-1)}
              className="w-7 h-7 rounded-lg border border-[#E5E3DB] flex items-center justify-center hover:bg-[#F3F1EC]"
            >
              <CaretLeft size={14} className="text-[#6B7280]" />
            </button>
            <button
              data-testid="schedule-nav-today"
              onClick={() => {
                // Iter 33d Follow-up 3 — quick-return jumps to the
                // current real-time week. In Day mode it ALSO snaps the
                // visible day index to today's weekday (Sun→Fri so the
                // user lands on a weekday). Previously the button only
                // reset baseDate, leaving Day view stuck on whichever
                // day-index the user had been browsing.
                const now = new Date();
                setBaseDate(now);
                const dow = now.getDay(); // 0=Sun..6=Sat
                const todayIdx = dow === 0 ? 4 : Math.min(dow - 1, 4);
                setViewDayIdx(todayIdx);
                setBuilderResetSignal((n) => n + 1);
              }}
              className="px-3 py-1 text-xs font-medium text-[#1E3F33] bg-[#E8F0EB] rounded-lg hover:bg-[#d5e5d9]"
            >
              {viewMode === "month" ? "This Month" : viewMode === "day" ? "Today" : "This Week"}
            </button>
            <button
              data-testid="schedule-nav-next"
              onClick={() => navWeek(1)}
              className="w-7 h-7 rounded-lg border border-[#E5E3DB] flex items-center justify-center hover:bg-[#F3F1EC]"
            >
              <CaretRight size={14} className="text-[#6B7280]" />
            </button>
            <span className="text-sm text-[#1F2937] font-medium heading-font ml-1" data-testid="week-label">
              {viewMode === "month"
                ? new Date(baseDate.getFullYear(), baseDate.getMonth(), 1).toLocaleDateString("en-US", { month: "long", year: "numeric" })
                : `Week of ${new Date(ws + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
            </span>
          </div>
        </div>

        <TabsContent value="view">
          {!isWithinActivePeriod && activePeriod ? (
            // FP3-075D — The friendly OutsidePeriodCard now renders
            // for BOTH Simple Mode and Advanced Mode. Pete wanted
            // Advanced users to get the same helpful "Go to active
            // schedule period" / "Edit schedule dates" CTAs instead
            // of the terse dead-end copy.
            <OutsidePeriodCard
              variant="view"
              activePeriod={activePeriod}
              onJumpToActivePeriod={(p) =>
                setBaseDate(new Date(p.start_date + "T12:00:00"))
              }
            />
          ) : !activePeriod && isSimpleMode ? (
            // Simple Mode users with no active period yet get the
            // guided "Set your schedule dates" variant. Advanced
            // Mode without a period falls through to the normal
            // calendar render path so power users can still see the
            // grid and use the in-period setup elsewhere.
            <OutsidePeriodCard variant="view" activePeriod={null} />
          ) : viewMode === "month" ? (
            <ScheduleMonthView
              monthDates={monthDates}
              mySessions={mySessions}
              conflictSet={conflictSet}
              sharedConflictSet={sharedConflictSet}
              sharedSchoolSessions={showSchoolContext ? sharedSchoolSessions : []}
            />
          ) : (
            <ScheduleView
              viewMode={viewMode}
              viewDayIdx={viewDayIdx}
              setViewDayIdx={setViewDayIdx}
              setViewMode={setViewMode}
              weekDates={weekDates}
              mySessions={mySessions}
              conflictSet={conflictSet}
              sharedConflictSet={sharedConflictSet}
              sharedSchoolSessions={showSchoolContext ? sharedSchoolSessions : []}
              onSessionLogged={loadData}
            />
          )}
        </TabsContent>

        <TabsContent value="builder">
          {!isWithinActivePeriod && activePeriod ? (
            // FP3-075D — Same OutsidePeriodCard in both modes on
            // Builder. Advanced Mode users get the friendly CTAs
            // instead of the terse dead-end copy.
            <OutsidePeriodCard
              variant="builder"
              activePeriod={activePeriod}
              onJumpToActivePeriod={(p) =>
                setBaseDate(new Date(p.start_date + "T12:00:00"))
              }
            />
          ) : !activePeriod && isSimpleMode ? (
            <OutsidePeriodCard variant="builder" activePeriod={null} />
          ) : (
            // FP3-035F Issue 2 — Hard-block Schedule Builder mutations
            // for admin shadowing a provider. We keep the UI visible
            // (calm read-only banner already at the top of the layout)
            // but disable every interactive descendant via fieldset.
            <fieldset disabled={readOnly.active} className="contents" data-testid="builder-readonly-fieldset">
              <ScheduleBuilder
                provider={provider}
                allStudents={allStudents}
                sessions={sessions}
                conflictSet={conflictSet}
                sharedConflictSet={sharedConflictSet}
                sharedSchoolSessions={showSchoolContext ? sharedSchoolSessions : []}
                weekDates={weekDates}
                activePeriod={activePeriod}
                onSaved={loadData}
                resetSignal={builderResetSignal}
                wizardDraft={wizardDraft}
                onWizardDraftConsumed={() => setWizardDraft(null)}
                highlightBlockId={highlightBlockId}
                focusStudentId={focusStudentId}
                onOpenClearAll={() => setClearAllOpen(true)}
              />
            </fieldset>
          )}
        </TabsContent>

        {/* Iter 32 — Team tab removed from the provider's main schedule.
            The shared school-context overlay (toggled in Calendar/Builder)
            covers the cross-provider visibility need without making the
            user feel like they manage the whole school's schedule. */}

        <TabsContent value="conflicts">
          <ConflictsPanel
            personalConflicts={personalConflicts}
            sharedConflicts={sharedConflicts}
            weekDates={weekDates}
          />
        </TabsContent>

        <TabsContent value="provider-rules">
          <fieldset disabled={readOnly.active} className="contents" data-testid="provider-rules-readonly-fieldset">
            <ProviderConstraints />
          </fieldset>
        </TabsContent>

        <TabsContent value="school-rules">
          <fieldset disabled={readOnly.active} className="contents" data-testid="school-rules-readonly-fieldset">
            <ScheduleConstraints />
          </fieldset>
        </TabsContent>

        <TabsContent value="school-year">
          <fieldset disabled={readOnly.active} className="contents" data-testid="school-year-readonly-fieldset">
            <SchoolYearTab
              provider={provider}
              activePeriod={activePeriod}
              onPeriodsChanged={loadData}
            />
          </fieldset>
        </TabsContent>

        <TabsContent value="health">
          <fieldset disabled={readOnly.active} className="contents" data-testid="health-readonly-fieldset">
            <HealthPanel
              provider={provider}
              schedulePeriodId={activePeriod?.id}
              onCountChange={setHealthCount}
            />
          </fieldset>
        </TabsContent>
      </Tabs>

      <ScheduleWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        provider={provider}
        activePeriod={activePeriod}
        onAccept={(blocks, mode) => {
          if (mode === "overwrite") {
            // Schedule was already persisted by the wizard apply endpoint —
            // just reload the canvas so it reflects the freshly saved state.
            loadData();
          } else {
            // Coordinate mode: ship as unsaved drafts for tweak + Save.
            setWizardDraft({ blocks, appliedAt: Date.now() });
          }
          setActiveTab("builder");
        }}
      />
      {/* FP3-106C — Clear All modal (open state driven by Builder header). */}
      <ClearAllScheduleModal
        open={clearAllOpen}
        onOpenChange={setClearAllOpen}
        provider={provider}
        activePeriod={activePeriod}
        onCleared={async () => {
          // Wipe every trace of client-side draft/wizard state, force
          // the Builder to remount its local buffers via resetSignal,
          // close the Wizard if it happens to be open, and reload the
          // canvas so it reflects the cleared server state.
          setWizardDraft(null);
          setWizardOpen(false);
          setBuilderResetSignal((n) => n + 1);
          await loadData();
        }}
      />
    </div>
  );
}
